import type {
  AssetGraphQueryDto,
  ExternalNetworkEntityKind,
  ExternalNetworkStatsDto,
} from './external-network.dto';

export type ExternalNetworkGraphViewMode = 'full';

export type ExternalNetworkGraphSelectedNode = {
  id: string;
  kind: ExternalNetworkEntityKind;
};

const FULL_GRAPH_KINDS: ExternalNetworkEntityKind[] = [
  'NETBLOCK',
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
];

const FULL_GRAPH_LIMIT_NODES = 800;
const FULL_GRAPH_CLUSTER_THRESHOLD = 120;

export const EXPLORE_NEIGHBORHOOD_INITIAL_DEPTH = 2;
export const EXPLORE_NEIGHBORHOOD_MAX_DEPTH = 4;
const NEIGHBORHOOD_DEPTH_ONE_LIMIT_NODES = 60;
const NEIGHBORHOOD_GRAPH_LIMIT_NODES = 120;
const ROUTE_GRAPH_LIMIT_NODES = 80;
const ROUTE_GRAPH_LIMIT_EDGES_PER_NODE = 10;

export const resolveNeighborhoodLimitNodes = (depth: number): number =>
  depth <= 1
    ? NEIGHBORHOOD_DEPTH_ONE_LIMIT_NODES
    : NEIGHBORHOOD_GRAPH_LIMIT_NODES;

export const FULL_VIEW_NODE_THRESHOLD = 1000;

export function resolveDefaultExternalNetworkGraphViewMode(
  _stats: ExternalNetworkStatsDto | undefined,
): ExternalNetworkGraphViewMode {
  return 'full';
}

export function isExternalNetworkGraphViewMode(
  value: unknown,
): value is ExternalNetworkGraphViewMode {
  return value === 'full';
}

export function normalizeExternalNetworkGraphViewMode(
  value: unknown,
): ExternalNetworkGraphViewMode {
  if (value === 'full' || value === 'risk' || value === 'exposed') {
    return 'full';
  }

  return 'full';
}

export function buildExternalNetworkGraphQuery({
  neighborhoodRoot,
  neighborhoodDepth,
  expandedClusterIds,
}: {
  viewMode?: ExternalNetworkGraphViewMode;
  neighborhoodRoot: ExternalNetworkGraphSelectedNode | null;
  neighborhoodDepth: number;
  expandedClusterIds: string[];
}): Partial<AssetGraphQueryDto> {
  if (neighborhoodRoot) {
    return {
      mode: 'neighborhood',
      rootEntityId: neighborhoodRoot.id,
      rootEntityKind: neighborhoodRoot.kind,
      depth: neighborhoodDepth,
      limitNodes: resolveNeighborhoodLimitNodes(neighborhoodDepth),
      limitEdgesPerNode: 40,
      kinds: FULL_GRAPH_KINDS,
      riskOnly: false,
      clusterStrategy: 'none',
    };
  }

  return {
    mode: 'full',
    limitNodes: FULL_GRAPH_LIMIT_NODES,
    limitEdgesPerNode: 50,
    kinds: FULL_GRAPH_KINDS,
    clusterStrategy: 'kind',
    clusterThreshold: FULL_GRAPH_CLUSTER_THRESHOLD,
    expandedClusterIds:
      expandedClusterIds.length > 0 ? expandedClusterIds : undefined,
  };
}

export function buildExternalNetworkRouteGraphQuery({
  id,
  kind,
}: ExternalNetworkGraphSelectedNode): Partial<AssetGraphQueryDto> {
  return {
    mode: 'route',
    rootEntityId: id,
    rootEntityKind: kind,
    limitNodes: ROUTE_GRAPH_LIMIT_NODES,
    limitEdgesPerNode: ROUTE_GRAPH_LIMIT_EDGES_PER_NODE,
    kinds: FULL_GRAPH_KINDS,
    riskOnly: false,
    clusterStrategy: 'none',
  };
}
